/**
 * Slim REST client for tideway-api. Mirrors the token-injection pattern of the
 * production `frontend/src/services/api.ts` but only covers the endpoints
 * frontend-v2 needs in phase 1.
 */
import type {
  ApiBooking,
  BatchWithAttachments,
  BlGroup,
  BookingBlDocument,
  BookingDocument,
  BookingPricingLine,
  BookingLedgerEntry,
  InvoiceListParams,
  InvoiceReconciliationDetail,
  InvoiceReconciliationListResponse,
  UnassignedDocumentRow,
  InvoiceUploadBatch,
  InvoiceUploadBatchDetail,
  InvoiceLineDetail,
  InvoiceUploadLine,
  InvoiceUploadLineRateMatch,
  AcceptReadyResponse,
  BookingGroupRow,
  BookingsFilters,
  CarrierTrackingResponse,
  ScheduleResponse,
  ScheduleSearchParams,
  PaginatedResponse,
  DdPolicyListResponse,
  RateCardDetail,
  RateCardsFilters,
  RateCardSummary,
  RateCardBookingItem,
  RateCardBookingsParams,
  RateLineFilters,
  RateLineGroupRow,
  RateLineItem,
  RateLineSearchParams,
  RateLineSearchResult,
  RateCardCreatePayload,
  RateCardCreateResponse,
  RateCardGroupRow,
  RateCardStats,
  AllocationRuleCreateData,
  PortGroup,
  ContainerTareEntry,
  VesselInfo,
  VesselLookupParams,
  PortAreaExpansionResponse,
  BookingOpportunityContext,
  RecommendationItem,
  UpdateRecommendationStatusResponse,
  RebookChainEntry,
  RecommendationQueryParams,
  SearchCycleSummary,
  StateTransitionEvent,
  CarrierInfo,
  SurchargeCodeAlias,
  SwitchableOrganization,
  TrackingBookingPortsResponse,
  TrackingPortBookingsResponse,
  TrackingResolveResponse,
  GlobeAnnotationLayersResponse,
  GlobeAnnotationsResponse,
  TrackingVesselsResponse,
  UserBookingAssignment,
  UserProfile,
  SupplyChainEntity,
} from "@/types/api";
import type {
  RestrictionConfig,
  RestrictionUpdate,
} from "@/types/restrictions";
import type {
  KaiCustomerRequest,
  KaiCustomerRequestListFilters,
  KaiCustomerRequestTriagePayload,
} from "@/types/customerRequest";
import type {
  CreateAssignmentPayload,
  CreatePricingTierPayload,
  PricingTier,
  PricingTierAssignment,
  PricingTierDimensions,
  UpdatePricingTierPayload,
} from "@/types/pricingTier";
import type { QuotaProgress, SalesQuotaTarget } from "@/types/salesQuota";
import type {
  DashboardLayout,
  FilterConfig,
  MetricsCatalog,
  MetricsQueryResponse,
  ReportSummary,
  WidgetConfig,
} from "@/types/metrics";
import type {
  FeedEventDetail,
  FeedGraphMailBody,
  FeedListParams,
  FeedPaginatedResponse,
  FeedSseEvent,
  OcfReconciliationDrillResponse,
  RateIngestLaneHistoryParams,
  RateIngestLaneHistoryResponse,
  RateIngestPoliciesResponse,
} from "@/types/feed";
import type { HomeLayout, HomeWidget } from "@/types/home";
import type {
  CreateDashboardSchedulePayload,
  CreateReportSchedulePayload,
  DashboardSchedule,
  OrganizationMember,
  ReportBrowseResponse,
  ReportSchedule,
  ReportSubscription,
  UpdateReportSchedulePayload,
} from "@/types/emailReports";
import type {
  CreateGraphMailboxPayload,
  CreateIntegrationPayload,
  CreateRpaEnrollmentCodePayload,
  CreateRpaJobPayload,
  GraphAdminConsentResponse,
  GraphAuthorizeResponse,
  GraphClassificationStats,
  GraphMailbox,
  GraphMailFolder,
  GraphMessageDetail,
  GraphMessageList,
  GraphSyncResult,
  Integration,
  IntegrationStatus,
  MyGraphMailboxStatus,
  RpaAgent,
  RpaEnrollmentCodeResponse,
  RpaJob,
  RpaWorkflowRelease,
  UpdateGraphMailboxPayload,
  UpdateIntegrationPayload,
  UploadRpaWorkflowBundlePayload,
  RpaWorkflowBundlesResponse,
} from "@/types/integration";
import {
  cached,
  CACHE_TTL,
  invalidateCached,
  invalidateCachedPrefix,
} from "@/lib/requestCache";

/** Shared cache keys — exported for stale-while-revalidate in `useHomeEditor`. */
export const HOME_LAYOUT_CACHE_KEY = "home:layout";
export const METRICS_CATALOG_CACHE_KEY = "metrics:catalog";
export const FEED_LIST_CACHE_PREFIX = "feed:list:";

/** Stable first-page key for `GET /v1/feed` (cursor pages are not cached). */
export function feedListCacheKey(params: FeedListParams = {}): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (key === "cursor") continue;
    query.set(key, String(value));
  }
  query.sort();
  return `${FEED_LIST_CACHE_PREFIX}${query.toString()}`;
}
import {
  applyViewerScopeToBookings,
  applyViewerScopeToRecommendations,
  applyViewerScopeToRecord,
  EMPTY_VIEWER_SCOPE,
  type ViewerBookingScope,
} from "@/lib/viewerScopeFilters";

type TokenGetter = () => Promise<string>;

/** Response of `GET /v1/push/vapid-public-key`. */
export interface VapidPublicKeyResponse {
  /** URL-safe base64 VAPID public key, or null when push is unconfigured. */
  key: string | null;
}

/** The caller's feed push preferences (`GET/PUT /v1/feed/push-preferences`). */
export interface FeedPushPreferences {
  enabled: boolean;
  rebooks_enabled: boolean;
  first_books_enabled: boolean;
  new_bookings_enabled: boolean;
  batch_ingested_enabled: boolean;
  urgent_warning_enabled: boolean;
  /** Explicit opt-in for favorite-booking watch updates. */
  favorites_watch_enabled: boolean;
  /** Backoffice custom-event feed cards (on by default with master push). */
  custom_events_enabled: boolean;
  /** Whether the server can actually send push (VAPID configured). */
  push_supported: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";

function bookingsFiltersToParams(
  filters: BookingsFilters & { group_by?: string },
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  return params;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

class ApiClient {
  private baseUrl: string;
  private getToken: TokenGetter | null = null;
  private viewerScope: ViewerBookingScope = EMPTY_VIEWER_SCOPE;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenGetter(getter: TokenGetter) {
    this.getToken = getter;
  }

  /** Linked-bookings scope applied to every bookings/metrics/recommendations call. */
  setViewerScope(scope: ViewerBookingScope) {
    this.viewerScope = scope;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      // Let token-acquisition errors propagate: the token getter redirects to
      // login on silent-auth failure, so we must not fire a token-less request.
      const token = await this.getToken();
      authHeaders["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: `HTTP ${response.status}` }));
      throw new ApiError(
        body.message || body.error || `HTTP ${response.status}`,
        response.status,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  // ── Users ─────────────────────────────────────────────────────────────

  async getMe(): Promise<UserProfile> {
    return this.request<UserProfile>("/v1/users/me");
  }

  async getMyOrganizations(): Promise<SwitchableOrganization[]> {
    return this.request<SwitchableOrganization[]>("/v1/users/me/organizations");
  }

  /** Resolved feature flags for the authenticated tenant (overrides applied). */
  async getResolvedFeatureFlags(): Promise<Record<string, boolean>> {
    return this.request<Record<string, boolean>>("/v1/feature-switches/resolved");
  }

  /** Standard surcharge code → display description map. */
  async getSurchargeCodes(): Promise<Record<string, string>> {
    return cached(
      "surcharge-codes",
      () => this.request<Record<string, string>>("/v1/mappings/surcharge-codes"),
      CACHE_TTL.reference,
    );
  }

  /** Carrier-specific surcharge spellings → standard codes. */
  async getSurchargeCodeAliases(): Promise<SurchargeCodeAlias[]> {
    return cached(
      "surcharge-code-aliases",
      () =>
        this.request<SurchargeCodeAlias[]>("/v1/mappings/surcharge-codes/aliases"),
      CACHE_TTL.reference,
    );
  }

  /** Distinct canonical agent slugs (`GET /v1/mappings/agents`). */
  async getAgentAliases(): Promise<Record<string, string>> {
    return cached(
      "agent-aliases",
      () => this.request<Record<string, string>>("/v1/mappings/agents"),
      CACHE_TTL.reference,
    );
  }

  /** User-defined port groups for trade-lane scoping. */
  async getPortGroups(): Promise<PortGroup[]> {
    return cached(
      "port-groups",
      () => this.request<PortGroup[]>("/v1/allocations/port-groups"),
      CACHE_TTL.reference,
    );
  }

  /** Container tare-weight registry, optionally filtered by carrier. */
  async getContainerTares(carrier?: string): Promise<ContainerTareEntry[]> {
    const qs = carrier ? `?carrier=${encodeURIComponent(carrier)}` : "";
    return this.request<ContainerTareEntry[]>(
      `/v1/mappings/container-tares${qs}`,
    );
  }

  /** Known sailings for a lane (`GET /v1/vessels`) — powers vessel-spot. */
  async getVessels(params: VesselLookupParams): Promise<VesselInfo[]> {
    const qs = new URLSearchParams();
    qs.set("origin", params.origin);
    qs.set("destination", params.destination);
    if (params.carrier) qs.set("carrier", params.carrier);
    if (params.etd_from) qs.set("etd_from", params.etd_from);
    if (params.etd_to) qs.set("etd_to", params.etd_to);
    return this.request<VesselInfo[]>(`/v1/vessels?${qs.toString()}`);
  }

  /** Create or update an allocation (TEU) rule. `POST /v1/allocations/rules`. */
  async upsertAllocationRule(
    data: AllocationRuleCreateData & { rule_type?: "route" | "contract" },
  ): Promise<unknown> {
    return this.request<unknown>("/v1/allocations/rules", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /** Active + inactive rows from the DB carrier registry. */
  async getCarriersRegistry(): Promise<CarrierInfo[]> {
    return cached(
      "carriers-registry",
      () => this.request<CarrierInfo[]>("/v1/mappings/carriers-registry"),
      CACHE_TTL.reference,
    );
  }

  // ── Domain event feed ─────────────────────────────────────────────────

  async getFeed(
    params: FeedListParams = {},
  ): Promise<FeedPaginatedResponse> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      query.set(key, String(value));
    }
    const qs = query.toString();
    const request = () =>
      this.request<FeedPaginatedResponse>(`/v1/feed${qs ? `?${qs}` : ""}`);
    // Only the first page is reused across hover-prefetch / splash / mount.
    if (params.cursor) return request();
    return cached(feedListCacheKey(params), request, CACHE_TTL.list);
  }

  async getFeedEvent(
    id: string,
    scope?: Pick<
      FeedListParams,
      "scope_dimension" | "scope_key" | "portfolio_intersection"
    >,
  ): Promise<FeedEventDetail> {
    const query = new URLSearchParams();
    if (scope?.scope_dimension) {
      query.set("scope_dimension", scope.scope_dimension);
    }
    if (scope?.scope_key) query.set("scope_key", scope.scope_key);
    if (scope?.portfolio_intersection === false) {
      query.set("portfolio_intersection", "false");
    }
    const qs = query.toString();
    return this.request<FeedEventDetail>(
      `/v1/feed/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}`,
    );
  }

  /** Weekly FRT history + policy snapshots for one lane on a rate-ingest card. */
  async getFeedRateIngestLaneHistory(
    eventId: string,
    params: RateIngestLaneHistoryParams,
  ): Promise<RateIngestLaneHistoryResponse> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      query.set(key, String(value));
    }
    const qs = query.toString();
    return this.request<RateIngestLaneHistoryResponse>(
      `/v1/feed/${encodeURIComponent(eventId)}/rate-ingest/lane-history${qs ? `?${qs}` : ""}`,
    );
  }

  /** Card-level policy snapshots for a rate-ingest feed event. */
  async getFeedRateIngestPolicies(
    eventId: string,
  ): Promise<RateIngestPoliciesResponse> {
    return this.request<RateIngestPoliciesResponse>(
      `/v1/feed/${encodeURIComponent(eventId)}/rate-ingest/policies`,
    );
  }

  /** Per-booking mismatch groups for an OCF reconciliation alert card. */
  async getFeedOcfReconciliation(
    eventId: string,
  ): Promise<OcfReconciliationDrillResponse> {
    return this.request<OcfReconciliationDrillResponse>(
      `/v1/feed/${encodeURIComponent(eventId)}/ocf-reconciliation`,
    );
  }

  async getFeedGraphMailBody(
    mailboxId: string,
    messageId: string,
  ): Promise<FeedGraphMailBody> {
    return this.request<FeedGraphMailBody>(
      `/v1/feed/graph-mail/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}`,
    );
  }

  /** Mark events reviewed for the caller (personal declutter). */
  async reviewFeedEvents(ids: string[]): Promise<{ reviewed: number }> {
    const result = await this.request<{ reviewed: number }>("/v1/feed/review", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
    invalidateCachedPrefix(FEED_LIST_CACHE_PREFIX);
    return result;
  }

  /**
   * Subscribe to the feed SSE stream. Uses fetch + a ReadableStream reader
   * (rather than `EventSource`) so the Auth0 bearer token can be sent as a
   * header. `onEvent` fires once per new root event. Returns when the stream
   * closes or `signal` aborts.
   */
  async streamFeed(
    onEvent: (event: FeedSseEvent) => void,
    signal?: AbortSignal,
    params: FeedListParams = {},
  ): Promise<void> {
    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      const token = await this.getToken();
      authHeaders["Authorization"] = `Bearer ${token}`;
    }

    // Forward the same scope/horizon filters as the list endpoint so the live
    // stream only pushes events matching the view the user is looking at.
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      if (key === "limit" || key === "cursor") continue;
      query.set(key, String(value));
    }
    const qs = query.toString();

    const response = await fetch(
      `${this.baseUrl}/v1/feed/stream${qs ? `?${qs}` : ""}`,
      {
        headers: { Accept: "text/event-stream", ...authHeaders },
        signal,
      },
    );

    if (!response.ok || !response.body) {
      throw new ApiError(
        `Feed stream failed: HTTP ${response.status}`,
        response.status,
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "";
    let currentData = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          currentData = line.slice(5).trim();
        } else if (line === "" && currentEvent) {
          if (currentEvent === "feed" && currentData) {
            try {
              onEvent(JSON.parse(currentData) as FeedSseEvent);
            } catch {
              // Ignore malformed frames (e.g. heartbeats) rather than break.
            }
          }
          currentEvent = "";
          currentData = "";
        }
      }
    }
  }

  // ── Bookings ──────────────────────────────────────────────────────────

  async getBookings(
    filters: BookingsFilters = {},
  ): Promise<PaginatedResponse<ApiBooking>> {
    const params = bookingsFiltersToParams(
      applyViewerScopeToBookings(filters, this.viewerScope),
    );
    const query = params.toString();
    return this.request<PaginatedResponse<ApiBooking>>(
      `/v1/bookings${query ? `?${query}` : ""}`,
    );
  }

  async getBookingsGrouped(
    filters: BookingsFilters & { group_by: string },
  ): Promise<PaginatedResponse<BookingGroupRow>> {
    const scoped = applyViewerScopeToBookings(filters, this.viewerScope);
    const params = bookingsFiltersToParams(scoped);
    params.set("group_by", filters.group_by);
    return this.request<PaginatedResponse<BookingGroupRow>>(
      `/v1/bookings/grouped?${params}`,
    );
  }

  async getBooking(sourceId: string): Promise<ApiBooking> {
    return cached(
      `booking:${sourceId}`,
      () =>
        this.request<ApiBooking>(`/v1/bookings/${encodeURIComponent(sourceId)}`),
      CACHE_TTL.detail,
    );
  }

  /** The caller's favorited ("starred") booking source_ids. */
  async getFavoriteBookings(): Promise<string[]> {
    const res = await this.request<{ source_ids: string[] }>(
      "/v1/bookings/favorites",
    );
    return res.source_ids ?? [];
  }

  /** Star or unstar a booking for the caller. Returns the resulting state. */
  async setBookingFavorite(
    sourceId: string,
    favorite: boolean,
  ): Promise<boolean> {
    const res = await this.request<{ source_id: string; is_favorite: boolean }>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/favorite`,
      {
        method: "PUT",
        body: JSON.stringify({ favorite }),
      },
    );
    // The cached detail view carries a stale `is_favorite`; drop it so the next
    // read reflects the new state.
    invalidateCached(`booking:${sourceId}`);
    return res.is_favorite;
  }

  async getBookingPricing(sourceId: string): Promise<BookingPricingLine[]> {
    return this.request<BookingPricingLine[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/pricing`,
    );
  }

  async getBookingEvents(sourceId: string): Promise<StateTransitionEvent[]> {
    return this.request<StateTransitionEvent[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/events`,
    );
  }

  async getBookingDocuments(sourceId: string): Promise<BookingDocument[]> {
    return this.request<BookingDocument[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/documents`,
    );
  }

  /** BL group this booking belongs to, or `null` when it is not grouped. */
  async getBookingBlGroup(sourceId: string): Promise<BlGroup | null> {
    return cached(
      `booking-bl-group:${sourceId}`,
      () =>
        this.request<BlGroup | null>(
          `/v1/bookings/${encodeURIComponent(sourceId)}/bl-group`,
        ),
      CACHE_TTL.detail,
    );
  }

  async getBookingLedger(sourceId: string): Promise<BookingLedgerEntry[]> {
    return this.request<BookingLedgerEntry[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/ledger`,
    );
  }

  // ── Invoices (invoice reconciliation) ───────────────────────────────────

  /** List the Invoices work queue (delivered bookings + billing docs). */
  async getInvoices(
    params: InvoiceListParams = {},
  ): Promise<InvoiceReconciliationListResponse> {
    const qs = new URLSearchParams();
    if (params.queue) qs.set("queue", params.queue);
    if (params.search) qs.set("search", params.search);
    if (params.linked_scope) qs.set("linked_scope", "true");
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return this.request<InvoiceReconciliationListResponse>(
      `/v1/invoices${query ? `?${query}` : ""}`,
    );
  }

  /** Detail view: persisted workflow + live computed diff snapshot. */
  async getInvoiceDetail(
    sourceId: string,
  ): Promise<InvoiceReconciliationDetail> {
    return this.request<InvoiceReconciliationDetail>(
      `/v1/invoices/${encodeURIComponent(sourceId)}`,
    );
  }

  /** Approve a booking's reconciliation (diff within tolerance / signed off). */
  async approveInvoice(
    sourceId: string,
  ): Promise<InvoiceReconciliationDetail> {
    return this.request<InvoiceReconciliationDetail>(
      `/v1/invoices/${encodeURIComponent(sourceId)}/approve`,
      { method: "POST" },
    );
  }

  /** Record a written explanation for a persistent diff. */
  async explainInvoice(
    sourceId: string,
    explanation: string,
  ): Promise<InvoiceReconciliationDetail> {
    return this.request<InvoiceReconciliationDetail>(
      `/v1/invoices/${encodeURIComponent(sourceId)}/explain`,
      { method: "POST", body: JSON.stringify({ explanation }) },
    );
  }

  /** Search extracted billing PDFs that can be assigned to a booking. */
  async getUnassignedInvoiceDocuments(params: {
    search?: string;
    mailboxId?: string;
    limit?: number;
  } = {}): Promise<UnassignedDocumentRow[]> {
    const qs = new URLSearchParams();
    if (params.search) qs.set("search", params.search);
    if (params.mailboxId) qs.set("mailbox_id", params.mailboxId);
    if (params.limit != null) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return this.request<UnassignedDocumentRow[]>(
      `/v1/invoices/unassigned-documents${query ? `?${query}` : ""}`,
    );
  }

  /** Assign (link) an extracted billing document to a booking. */
  async assignInvoiceDocument(
    sourceId: string,
    jobId: string,
  ): Promise<InvoiceReconciliationDetail> {
    return this.request<InvoiceReconciliationDetail>(
      `/v1/invoices/${encodeURIComponent(sourceId)}/assign`,
      { method: "POST", body: JSON.stringify({ job_id: jobId }) },
    );
  }

  /** Mark a ledger document active, silent, or deducted for reconciliation. */
  async setInvoiceDocumentDisposition(
    sourceId: string,
    ledgerId: string,
    disposition: "active" | "silent" | "deducted",
  ): Promise<InvoiceReconciliationDetail> {
    return this.request<InvoiceReconciliationDetail>(
      `/v1/invoices/${encodeURIComponent(sourceId)}/documents/${encodeURIComponent(ledgerId)}/disposition`,
      { method: "PATCH", body: JSON.stringify({ disposition }) },
    );
  }

  // ── Invoice batch upload ────────────────────────────────────────────────

  /** List recent invoice upload batches for the tenant. */
  async listInvoiceBatches(): Promise<{ batches: InvoiceUploadBatch[] }> {
    return this.request<{ batches: InvoiceUploadBatch[] }>(
      `/v1/invoices/batches`,
    );
  }

  /** Create a new upload batch for a carrier. */
  async createInvoiceBatch(
    carrier: string,
    label?: string,
  ): Promise<InvoiceUploadBatch> {
    return this.request<InvoiceUploadBatch>(`/v1/invoices/batches`, {
      method: "POST",
      body: JSON.stringify({ carrier, label: label || null }),
    });
  }

  /** Fetch a batch with its lines (folds in newly-terminal extract jobs). */
  async getInvoiceBatch(
    batchId: string,
    opts: { includeRateMatch?: boolean } = {},
  ): Promise<InvoiceUploadBatchDetail> {
    const params = new URLSearchParams();
    if (opts.includeRateMatch) {
      params.set("include_rate_match", "true");
    }
    const query = params.toString();
    return this.request<InvoiceUploadBatchDetail>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}${query ? `?${query}` : ""}`,
    );
  }

  /** Rate-match enrichment for all lines (run after a lightweight batch GET). */
  async getInvoiceBatchLineRateMatch(
    batchId: string,
  ): Promise<{ lines: InvoiceUploadLineRateMatch[] }> {
    return this.request<{ lines: InvoiceUploadLineRateMatch[] }>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/line-rate-match`,
    );
  }

  /** Upload one or more PDFs into a batch (multipart). */
  async uploadInvoiceBatchFiles(
    batchId: string,
    files: File[],
  ): Promise<InvoiceUploadBatchDetail> {
    const url = `${this.baseUrl}/v1/invoices/batches/${encodeURIComponent(batchId)}/files`;
    const form = new FormData();
    for (const file of files) form.append("files", file, file.name);
    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      const token = await this.getToken();
      authHeaders.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders, // let the browser set the multipart boundary
      body: form,
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: `HTTP ${response.status}` }));
      throw new ApiError(
        body.message || body.error || `HTTP ${response.status}`,
        response.status,
      );
    }
    return response.json();
  }

  private invoiceLineDetailCacheKey(batchId: string, lineId: string): string {
    return `invoice-upload-line-detail:${batchId}:${lineId}`;
  }

  private invoiceLinePdfCacheKey(batchId: string, lineId: string): string {
    return `invoice-upload-line-pdf:${batchId}:${lineId}`;
  }

  private invalidateInvoiceLineDetailCache(
    batchId: string,
    lineId: string,
  ): void {
    invalidateCached(this.invoiceLineDetailCacheKey(batchId, lineId));
    invalidateCached(this.invoiceLinePdfCacheKey(batchId, lineId));
  }

  /** Drop cached line detail/PDF after mutations or terminal extraction. */
  invalidateInvoiceLineDetail(batchId: string, lineId: string): void {
    this.invalidateInvoiceLineDetailCache(batchId, lineId);
  }

  /** Line detail with rate / PCD pricing context. */
  async getInvoiceLineDetail(
    batchId: string,
    lineId: string,
    opts?: { bypassCache?: boolean },
  ): Promise<InvoiceLineDetail> {
    const fetch = () =>
      this.request<InvoiceLineDetail>(
        `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/detail`,
      );
    if (opts?.bypassCache) return fetch();
    return cached(
      this.invoiceLineDetailCacheKey(batchId, lineId),
      fetch,
      CACHE_TTL.detail,
    );
  }

  /** Set (or clear) the user's booking override for a line. */
  async setInvoiceLineBooking(
    batchId: string,
    lineId: string,
    sourceId: string | null,
  ): Promise<InvoiceUploadLine> {
    this.invalidateInvoiceLineDetailCache(batchId, lineId);
    return this.request<InvoiceUploadLine>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/booking`,
      { method: "PATCH", body: JSON.stringify({ source_id: sourceId }) },
    );
  }

  /** Accept a single ready line into the ledger. */
  async acceptInvoiceLine(
    batchId: string,
    lineId: string,
    sourceId?: string,
  ): Promise<InvoiceUploadLine> {
    this.invalidateInvoiceLineDetailCache(batchId, lineId);
    return this.request<InvoiceUploadLine>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/accept`,
      {
        method: "POST",
        body: JSON.stringify(sourceId ? { source_id: sourceId } : {}),
      },
    );
  }

  /** Skip a line (no ledger row). */
  async skipInvoiceLine(
    batchId: string,
    lineId: string,
  ): Promise<InvoiceUploadLine> {
    this.invalidateInvoiceLineDetailCache(batchId, lineId);
    return this.request<InvoiceUploadLine>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/skip`,
      { method: "POST" },
    );
  }

  /** Re-run doc-intel on the uploaded PDF (not allowed after accept). */
  async reprocessInvoiceLine(
    batchId: string,
    lineId: string,
  ): Promise<InvoiceLineDetail> {
    this.invalidateInvoiceLineDetailCache(batchId, lineId);
    return this.request<InvoiceLineDetail>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/reprocess`,
      { method: "POST" },
    );
  }

  /**
   * Enrich a Maersk API invoice line: dispatch the collector workflow that
   * captures a portal Bearer, downloads the invoice PDF + charge detail, and
   * enqueues doc-intel. Replaces plain reprocess for Maersk API batches (there
   * is no uploaded PDF to re-extract).
   */
  async enrichMaerskInvoiceLine(
    batchId: string,
    lineId: string,
  ): Promise<InvoiceLineDetail> {
    this.invalidateInvoiceLineDetailCache(batchId, lineId);
    return this.request<InvoiceLineDetail>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/enrich-maersk`,
      { method: "POST" },
    );
  }

  /** Accept every ready line with a resolvable booking. */
  async acceptReadyInvoiceLines(
    batchId: string,
  ): Promise<AcceptReadyResponse> {
    return this.request<AcceptReadyResponse>(
      `/v1/invoices/batches/${encodeURIComponent(batchId)}/accept-ready`,
      { method: "POST" },
    );
  }

  /** Authenticated URL fetch for an upload line's source PDF (blob). */
  async fetchInvoiceLinePdf(
    batchId: string,
    lineId: string,
  ): Promise<Blob> {
    return cached(
      this.invoiceLinePdfCacheKey(batchId, lineId),
      () =>
        this.fetchAuthedBlob(
          `/v1/invoices/batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}/pdf`,
        ),
      CACHE_TTL.detail,
    );
  }

  /** Authenticated fetch for a ledger document's source PDF (blob). */
  async fetchLedgerDocumentPdf(ledgerId: string): Promise<Blob> {
    return this.fetchAuthedBlob(
      `/v1/invoices/documents/${encodeURIComponent(ledgerId)}/pdf`,
    );
  }

  /** Shared authenticated blob GET. */
  private async fetchAuthedBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      const token = await this.getToken();
      authHeaders.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      const detail =
        response.status === 403
          ? "You do not have permission to open this file."
          : response.status === 404
            ? "This file is no longer available."
            : `Failed to load PDF: HTTP ${response.status}`;
      throw new ApiError(detail, response.status);
    }
    return response.blob();
  }

  /** Bills of lading doc-intel extracted and linked to this booking. */
  async getBookingBlDocuments(sourceId: string): Promise<BookingBlDocument[]> {
    return cached(
      `booking-bl-documents:${sourceId}`,
      () =>
        this.request<BookingBlDocument[]>(
          `/v1/bookings/${encodeURIComponent(sourceId)}/bl-documents`,
        ),
      CACHE_TTL.detail,
    );
  }

  async getBookingRebookChain(sourceId: string): Promise<RebookChainEntry[]> {
    return this.request<RebookChainEntry[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/rebook-chain`,
    );
  }

  async getBookingSchedules(
    sourceId: string,
    params?: { include_all_carriers?: boolean },
  ): Promise<ScheduleResponse[]> {
    const qs = new URLSearchParams();
    if (params?.include_all_carriers) qs.set("include_all_carriers", "true");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.request<ScheduleResponse[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/schedules${suffix}`,
    );
  }

  /** Cross-route Schedule Lookup — `GET /v1/schedules/search`. */
  async searchSchedules(
    params: ScheduleSearchParams,
  ): Promise<PaginatedResponse<ScheduleResponse>> {
    const qs = new URLSearchParams();
    if (params.origin_port) qs.set("origin_port", params.origin_port);
    if (params.dest_port) qs.set("dest_port", params.dest_port);
    if (params.carrier) qs.set("carrier", params.carrier);
    if (params.date_from) qs.set("date_from", params.date_from);
    if (params.date_to) qs.set("date_to", params.date_to);
    if (params.cutoff_from) qs.set("cutoff_from", params.cutoff_from);
    if (params.cutoff_to) qs.set("cutoff_to", params.cutoff_to);
    if (params.page) qs.set("page", String(params.page));
    qs.set("per_page", String(params.per_page ?? 200));
    return this.request<PaginatedResponse<ScheduleResponse>>(
      `/v1/schedules/search?${qs.toString()}`,
    );
  }

  async getBookingTracking(
    sourceId: string,
  ): Promise<CarrierTrackingResponse[]> {
    return this.request<CarrierTrackingResponse[]>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/tracking`,
    );
  }

  // ── Live vessel tracking (AIS globe) ──────────────────────────────────

  /** Active booking vessels for the tracking globe's vessel list. */
  async getTrackingVessels(): Promise<TrackingVesselsResponse> {
    return this.request<TrackingVesselsResponse>("/v1/tracking/vessels");
  }

  /**
   * Unified tracking lookup: resolve a vessel name/IMO, booking ref, carrier
   * B/L, file id, or source id to matching booking(s), each with resolved IMO,
   * latest AIS fix, and planned + progress voyage lines.
   */
  async resolveTracking(
    q: string,
    limit = 8,
  ): Promise<TrackingResolveResponse> {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    return this.request<TrackingResolveResponse>(
      `/v1/tracking/resolve?${qs.toString()}`,
    );
  }

  /** Active bookings on one fleet vessel, with voyage routes for the globe. */
  async getVesselBookings(params: {
    imo?: number | null;
    vessel?: string | null;
    limit?: number;
  }): Promise<TrackingResolveResponse> {
    const qs = new URLSearchParams();
    if (params.imo != null) qs.set("imo", String(params.imo));
    if (params.vessel?.trim()) qs.set("vessel", params.vessel.trim());
    if (params.limit != null) qs.set("limit", String(params.limit));
    return this.request<TrackingResolveResponse>(
      `/v1/tracking/vessel-bookings?${qs.toString()}`,
    );
  }

  /** POL/POD ports for active bookings (respects fleet filter). */
  async getTrackingBookingPorts(
    fleet: "all" | "sailing" | "scheduled" = "all",
  ): Promise<TrackingBookingPortsResponse> {
    const qs = new URLSearchParams({ fleet });
    return this.request(`/v1/tracking/booking-ports?${qs.toString()}`);
  }

  /** Up to 5 bookings at a port (respects fleet filter). */
  async getTrackingPortBookings(
    locode: string,
    fleet: "all" | "sailing" | "scheduled" = "all",
    limit = 5,
  ): Promise<TrackingPortBookingsResponse> {
    const qs = new URLSearchParams({ fleet, limit: String(limit) });
    return this.request(
      `/v1/tracking/booking-ports/${encodeURIComponent(locode)}/bookings?${qs.toString()}`,
    );
  }

  /** Metadata for all globe annotation layers (toggle control). */
  async getTrackingAnnotationLayers(): Promise<GlobeAnnotationLayersResponse> {
    return this.request<GlobeAnnotationLayersResponse>(
      "/v1/tracking/annotation-layers",
    );
  }

  /** Requested annotation layers with their geometry + hover metadata. */
  async getTrackingAnnotations(
    layerIds: string[],
  ): Promise<GlobeAnnotationsResponse> {
    if (layerIds.length === 0) return { layers: [] };
    const qs = new URLSearchParams({ layers: layerIds.join(",") });
    return this.request<GlobeAnnotationsResponse>(
      `/v1/tracking/annotations?${qs.toString()}`,
    );
  }

  /**
   * Build the authenticated WebSocket URL for the live AIS position stream.
   * The JWT rides in a `?token=` query param since browsers can't set headers
   * on WebSocket handshakes (mirrors the SSE token fallback in the API).
   */
  async createTrackingStreamUrl(): Promise<string> {
    const token = this.getToken ? await this.getToken() : "";
    const base = new URL(this.baseUrl, window.location.origin);
    const wsProtocol = base.protocol === "https:" ? "wss:" : "ws:";
    const path = `${base.pathname.replace(/\/$/, "")}/v1/tracking/stream`;
    const url = new URL(`${wsProtocol}//${base.host}${path}`);
    url.searchParams.set("token", token);
    return url.toString();
  }

  // ── Rate cards ────────────────────────────────────────────────────────

  /** Distinct carriers and contracts for rate-card filter autosuggest. */
  async getRateCardStats(): Promise<RateCardStats> {
    return cached(
      "rate-cards:stats",
      () => this.request<RateCardStats>("/v1/rate-cards/stats"),
      CACHE_TTL.reference,
    );
  }

  /** Distinct filter values for the rate cards list (e.g. service trade). */
  async getRateCardFilterValues(
    property: string,
    text?: string,
    limit = 50,
  ): Promise<{ value: string; label?: string | null }[]> {
    const params = new URLSearchParams({ property });
    if (text?.trim()) params.set("text", text.trim());
    params.set("limit", String(limit));
    return this.request(`/v1/rate-cards/filter-values?${params}`);
  }

  async getRateCards(
    filters: RateCardsFilters = {},
  ): Promise<PaginatedResponse<RateCardSummary>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === "") continue;
      params.set(key, String(value));
    }
    const query = params.toString();
    return this.request<PaginatedResponse<RateCardSummary>>(
      `/v1/rate-cards${query ? `?${query}` : ""}`,
    );
  }

  async getRateCard(id: string): Promise<RateCardDetail> {
    return cached(
      `rate-card:${id}`,
      () =>
        this.request<RateCardDetail>(`/v1/rate-cards/${encodeURIComponent(id)}`),
      CACHE_TTL.detail,
    );
  }

  /** Ingest batch metadata (attachment ids/filenames for source download). */
  async getBatch(batchId: string): Promise<BatchWithAttachments> {
    return this.request<BatchWithAttachments>(
      `/v1/batches/${encodeURIComponent(batchId)}`,
    );
  }

  async getRateCardLines(
    id: string,
    filters: RateLineFilters = {},
  ): Promise<PaginatedResponse<RateLineItem>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === "") continue;
      params.set(key, String(value));
    }
    const query = params.toString();
    return cached(
      `rate-card-lines:${id}?${query}`,
      () =>
        this.request<PaginatedResponse<RateLineItem>>(
          `/v1/rate-cards/${encodeURIComponent(id)}/lines${query ? `?${query}` : ""}`,
        ),
      CACHE_TTL.detail,
    );
  }

  async getRateCardLinesGrouped(
    id: string,
    filters: RateLineFilters & { group_by: string },
  ): Promise<PaginatedResponse<RateLineGroupRow>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === "") continue;
      params.set(key, String(value));
    }
    params.set("group_by", filters.group_by);
    return this.request<PaginatedResponse<RateLineGroupRow>>(
      `/v1/rate-cards/${encodeURIComponent(id)}/lines/grouped?${params}`,
    );
  }

  async getRateCardRevisions(contractRef: string): Promise<RateCardSummary[]> {
    return this.request<RateCardSummary[]>(
      `/v1/rate-cards/by-contract/${encodeURIComponent(contractRef)}`,
    );
  }

  /**
   * Bookings priced against or recommended for a rate card.
   * `GET /v1/rate-cards/:id/bookings`.
   */
  async getRateCardBookings(
    cardId: string,
    params: RateCardBookingsParams = {},
  ): Promise<PaginatedResponse<RateCardBookingItem>> {
    const qs = new URLSearchParams();
    if (params.assignment) qs.set("assignment", params.assignment);
    if (params.page != null) qs.set("page", String(params.page));
    if (params.per_page != null) qs.set("per_page", String(params.per_page));
    const query = qs.toString();
    return this.request<PaginatedResponse<RateCardBookingItem>>(
      `/v1/rate-cards/${encodeURIComponent(cardId)}/bookings${query ? `?${query}` : ""}`,
    );
  }

  /** Create a rate card (manual / spot / revision). `POST /v1/rate-cards`. */
  async createRateCard(
    payload: RateCardCreatePayload,
  ): Promise<RateCardCreateResponse> {
    return this.request<RateCardCreateResponse>("/v1/rate-cards", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /** Grouped rate cards (`GET /v1/rate-cards/grouped`). */
  async getRateCardsGrouped(
    filters: RateCardsFilters & { group_by: string },
  ): Promise<PaginatedResponse<RateCardGroupRow>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === "") continue;
      params.set(key, String(value));
    }
    return this.request<PaginatedResponse<RateCardGroupRow>>(
      `/v1/rate-cards/grouped?${params}`,
    );
  }

  async listDdPoliciesByRateCard(
    rateCardId: string,
  ): Promise<DdPolicyListResponse> {
    return cached(
      `rate-card-dd-policies:${rateCardId}`,
      () =>
        this.request<DdPolicyListResponse>(
          `/v1/rate-cards/${encodeURIComponent(rateCardId)}/dd-policies`,
        ),
      CACHE_TTL.detail,
    );
  }

  /** Cross-card Rate Lookup — `GET /v1/rate-lines/search`. */
  async searchRateLines(
    params: RateLineSearchParams,
  ): Promise<PaginatedResponse<RateLineSearchResult>> {
    const qs = new URLSearchParams();
    if (params.origin_port) qs.set("origin_port", params.origin_port);
    if (params.destination_port)
      qs.set("destination_port", params.destination_port);
    if (params.equipment_type) qs.set("equipment_type", params.equipment_type);
    if (params.equipment_size) qs.set("equipment_size", params.equipment_size);
    if (params.carrier) qs.set("carrier", params.carrier);
    if (params.valid_on) qs.set("valid_on", params.valid_on);
    if (params.valid_overlap_from)
      qs.set("valid_overlap_from", params.valid_overlap_from);
    if (params.valid_overlap_to)
      qs.set("valid_overlap_to", params.valid_overlap_to);
    if (params.source_type) qs.set("source_type", params.source_type);
    if (params.source) qs.set("source", params.source);
    if (params.contract_ref) qs.set("contract_ref", params.contract_ref);
    if (params.load_type) qs.set("load_type", params.load_type);
    if (params.weight != null) qs.set("weight", String(params.weight));
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    const query = qs.toString();
    return this.request<PaginatedResponse<RateLineSearchResult>>(
      `/v1/rate-lines/search${query ? `?${query}` : ""}`,
    );
  }

  // ── Recommendations ───────────────────────────────────────────────────

  async searchPorts(
    query: string,
  ): Promise<{ locode: string; name: string; country?: string | null }[]> {
    return this.request(`/v1/ports/search?q=${encodeURIComponent(query)}`);
  }

  /**
   * Resolve one or more origin LOCODEs to their mapped port areas. Powers the
   * "mapped to what" banner and per-row area badges in Rate/Schedule Lookup.
   */
  async getPortAreaExpansion(
    ports: string,
  ): Promise<PortAreaExpansionResponse> {
    const qs = new URLSearchParams();
    qs.set("ports", ports);
    return this.request<PortAreaExpansionResponse>(
      `/v1/ports/area-expansion?${qs.toString()}`,
    );
  }

  async getSupplyChainEntities(
    entityType: string,
    params?: { search?: string; per_page?: number; page?: number },
  ): Promise<PaginatedResponse<SupplyChainEntity>> {
    const qs = new URLSearchParams();
    qs.set("entity_type", entityType);
    if (params?.search) qs.set("search", params.search);
    if (params?.per_page) qs.set("per_page", String(params.per_page));
    if (params?.page) qs.set("page", String(params.page));
    return this.request<PaginatedResponse<SupplyChainEntity>>(
      `/v1/supply-chain-entities?${qs}`,
    );
  }

  async getBookingFilterValues(
    property: string,
    text?: string,
    limit?: number,
    options?: { linkedScope?: boolean },
  ): Promise<{ value: string; label?: string }[]> {
    const params = new URLSearchParams();
    params.set("property", property);
    if (text) params.set("text", text);
    if (limit) params.set("limit", String(limit));
    if (options?.linkedScope) params.set("linked_scope", "true");
    return this.request(`/v1/bookings/filter-values?${params}`);
  }

  async getSearchCycles(params?: {
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<SearchCycleSummary>> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.per_page) qs.set("per_page", String(params.per_page));
    const query = qs.toString();
    return this.request<PaginatedResponse<SearchCycleSummary>>(
      `/v1/search-cycles${query ? `?${query}` : ""}`,
    );
  }

  private recommendationQuery(params?: RecommendationQueryParams): string {
    const qs = new URLSearchParams();
    const next = applyViewerScopeToRecommendations(params ?? {}, this.viewerScope);
    if (next.page) qs.set("page", String(next.page));
    if (next.per_page) qs.set("per_page", String(next.per_page));
    if (next.status) qs.set("status", next.status);
    if (next.status_not) qs.set("status_not", next.status_not);
    if (next.include_blocked !== undefined) {
      qs.set("include_blocked", String(next.include_blocked));
    }
    if (next.blocked_only) qs.set("blocked_only", String(next.blocked_only));
    if (next.search) qs.set("search", next.search);
    if (next.sort_by) qs.set("sort_by", next.sort_by);
    if (next.sort_dir) qs.set("sort_dir", next.sort_dir);
    if (next.opportunity_type) {
      qs.set("opportunity_type", next.opportunity_type);
    }
    if (next.booking_sales_person) {
      qs.set("booking_sales_person", next.booking_sales_person);
    }
    if (next.booking_opened_by) {
      qs.set("booking_opened_by", next.booking_opened_by);
    }
    if (next.linked_scope) {
      qs.set("linked_scope", String(next.linked_scope));
    }
    if (next.booking_service_trade) {
      qs.set("booking_service_trade", next.booking_service_trade);
    }
    if (next.booking_service_trade_not) {
      qs.set("booking_service_trade_not", next.booking_service_trade_not);
    }
    return qs.toString();
  }

  async getRecommendations(
    params?: RecommendationQueryParams,
  ): Promise<PaginatedResponse<RecommendationItem>> {
    const query = this.recommendationQuery(params);
    return this.request<PaginatedResponse<RecommendationItem>>(
      `/v1/recommendations${query ? `?${query}` : ""}`,
    );
  }

  async getSearchCycleRecommendations(
    cycleId: string,
    params?: RecommendationQueryParams,
  ): Promise<PaginatedResponse<RecommendationItem>> {
    const query = this.recommendationQuery(params);
    return this.request<PaginatedResponse<RecommendationItem>>(
      `/v1/search-cycles/${encodeURIComponent(cycleId)}/recommendations${
        query ? `?${query}` : ""
      }`,
    );
  }

  /** Rebook alternatives for one booking, newest / highest savings first. */
  async getBookingRecommendations(
    sourceId: string,
  ): Promise<RecommendationItem[]> {
    return cached(
      `booking-recs:${sourceId}`,
      () =>
        this.request<RecommendationItem[]>(
          `/v1/bookings/${encodeURIComponent(sourceId)}/recommendations`,
        ),
      CACHE_TTL.detail,
    );
  }

  /**
   * Why a booking has no rebook alternatives — powers the empty state.
   * Not cached: the answer depends on the latest search cycle, which changes
   * independently of the (cached) recommendations list.
   */
  async getBookingOpportunityContext(
    sourceId: string,
  ): Promise<BookingOpportunityContext> {
    return this.request<BookingOpportunityContext>(
      `/v1/bookings/${encodeURIComponent(sourceId)}/opportunity-context`,
    );
  }

  /**
   * Accept / dismiss / review a recommendation. Requires `create:rebooks`.
   * On accept with `sendEmail`, the response carries `emails_sent` outcomes.
   * Invalidates the cached per-booking recommendations so the rail reflects
   * the new status on the next read.
   */
  async updateRecommendationStatus(
    id: string,
    status: string,
    options?: { sendEmail?: boolean; sourceId?: string },
  ): Promise<UpdateRecommendationStatusResponse> {
    const result = await this.request<UpdateRecommendationStatusResponse>(
      `/v1/recommendations/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          status,
          ...(options?.sendEmail !== undefined
            ? { send_email: options.sendEmail }
            : {}),
        }),
      },
    );
    if (options?.sourceId) {
      invalidateCached(`booking-recs:${options.sourceId}`);
    }
    return result;
  }

  /**
   * The single active accepted rebook for a booking, or `null` when none.
   * Populates `workflow_stage` / `verification_state` for the accepted-rebook
   * card. Returns `null` on 404 (no active accepted rebook).
   */
  async getBookingAcceptedRebook(
    sourceId: string,
  ): Promise<RecommendationItem | null> {
    try {
      return await this.request<RecommendationItem>(
        `/v1/bookings/${encodeURIComponent(sourceId)}/accepted-rebook`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  // ── Metrics / configurable dashboards ─────────────────────────────────

  async getMetricsCatalog(): Promise<MetricsCatalog> {
    return cached(
      METRICS_CATALOG_CACHE_KEY,
      () => this.request<MetricsCatalog>("/v1/metrics/catalog"),
      CACHE_TTL.home,
    );
  }

  async queryMetrics(
    entity: string,
    kpi: string,
    filters?: Record<string, unknown>,
  ): Promise<MetricsQueryResponse> {
    const body = JSON.stringify({
      entity,
      kpi,
      filters: applyViewerScopeToRecord(filters, this.viewerScope),
    });
    // Dashboards mount many tiles at once; identical (entity, kpi, filters)
    // queries — common with duplicated KPIs and shared slicers — collapse to a
    // single request and a short-lived reuse window.
    return cached(
      `metrics:${body}`,
      () =>
        this.request<MetricsQueryResponse>("/v1/metrics/query", {
          method: "POST",
          body,
        }),
      CACHE_TTL.metric,
    );
  }

  async listReports(): Promise<ReportSummary[]> {
    const res = await this.request<{ data: ReportSummary[] }>(
      "/v1/metrics/reports",
    );
    return res.data;
  }

  async createReport(
    name: string,
    description = "",
    viewerRole: string | null = null,
    viewerUserIds?: string[],
  ): Promise<ReportSummary> {
    return this.request<ReportSummary>("/v1/metrics/reports", {
      method: "POST",
      body: JSON.stringify({
        name,
        description,
        viewer_role: viewerRole,
        ...(viewerUserIds !== undefined
          ? { viewer_user_ids: viewerUserIds }
          : {}),
      }),
    });
  }

  async updateReport(
    reportId: string,
    name: string,
    description = "",
    viewerRole?: string | null,
    viewerUserIds?: string[],
  ): Promise<void> {
    await this.request<unknown>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          name,
          description,
          ...(viewerRole !== undefined ? { viewer_role: viewerRole } : {}),
          ...(viewerUserIds !== undefined
            ? { viewer_user_ids: viewerUserIds }
            : {}),
        }),
      },
    );
  }

  async deleteReport(reportId: string): Promise<void> {
    await this.request<unknown>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}`,
      { method: "DELETE" },
    );
  }

  async getReportLayout(reportId: string): Promise<DashboardLayout> {
    return this.request<DashboardLayout>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/layout`,
    );
  }

  async saveReportLayout(
    reportId: string,
    layout: unknown,
    widgets: WidgetConfig[],
    filterConfig?: FilterConfig,
  ): Promise<void> {
    await this.request<unknown>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/layout`,
      {
        method: "PUT",
        body: JSON.stringify({
          layout,
          widgets,
          ...(filterConfig !== undefined ? { filter_config: filterConfig } : {}),
        }),
      },
    );
  }

  async getMyDashboardFilters(
    reportId: string,
  ): Promise<{ report_id: string; filters: Record<string, string> }> {
    return this.request<{ report_id: string; filters: Record<string, string> }>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/my-filters`,
    );
  }

  async saveMyDashboardFilters(
    reportId: string,
    filters: Record<string, string>,
  ): Promise<void> {
    await this.request<unknown>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/my-filters`,
      { method: "PUT", body: JSON.stringify({ filters }) },
    );
  }

  async getFilterValues(
    key: string,
    text?: string,
    options?: { linkedScope?: boolean },
  ): Promise<{ value: string; label?: string }[]> {
    const params = new URLSearchParams({ key });
    if (text) params.set("text", text);
    if (options?.linkedScope) params.set("linked_scope", "true");
    return this.request<{ value: string; label?: string }[]>(
      `/v1/metrics/filter-values?${params.toString()}`,
    );
  }

  // ── Dashboard email scheduling ────────────────────────────────────────

  /** Fetch the dashboard's email snapshot schedule (or null when unscheduled). */
  async getDashboardSchedule(
    reportId: string,
  ): Promise<DashboardSchedule | null> {
    const res = await this.request<{ schedule: DashboardSchedule | null }>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/schedule`,
    );
    return res.schedule ?? null;
  }

  /** Create or update the dashboard's email snapshot schedule (owner/admin). */
  async createDashboardSchedule(
    reportId: string,
    body: CreateDashboardSchedulePayload,
  ): Promise<DashboardSchedule> {
    return this.request<DashboardSchedule>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/schedule`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /** Remove the dashboard's email snapshot schedule (owner/admin). */
  async deleteDashboardSchedule(reportId: string): Promise<void> {
    await this.request<unknown>(
      `/v1/metrics/reports/${encodeURIComponent(reportId)}/schedule`,
      { method: "DELETE" },
    );
  }

  /**
   * Render a dashboard snapshot as an HTML email preview and trigger a browser
   * download. No email is sent.
   */
  async previewDashboardEmail(reportId: string): Promise<void> {
    const path = `/v1/metrics/reports/${encodeURIComponent(reportId)}/preview-email?format=html`;
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {};
    if (this.getToken) {
      try {
        const token = await this.getToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (err) {
        console.warn("Failed to obtain access token", err);
      }
    }
    const response = await fetch(url, { method: "POST", headers });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ message: "Preview failed" }));
      throw new ApiError(
        (err as { message?: string }).message || `HTTP ${response.status}`,
        response.status,
      );
    }
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = match?.[1] || "dashboard-preview.html";
    const blob = await response.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  }

  // ── Home dashboard (per-user customizable) ────────────────────────────

  async getHomeLayout(): Promise<HomeLayout> {
    return cached(
      HOME_LAYOUT_CACHE_KEY,
      () => this.request<HomeLayout>("/v1/home/layout"),
      CACHE_TTL.home,
    );
  }

  async saveHomeLayout(layout: unknown, widgets: HomeWidget[]): Promise<void> {
    await this.request<unknown>("/v1/home/layout", {
      method: "PUT",
      body: JSON.stringify({ layout, widgets }),
    });
    invalidateCached(HOME_LAYOUT_CACHE_KEY);
  }

  /** Replace the caller's Home layout with the default template for their role. */
  async resetHomeLayout(): Promise<HomeLayout> {
    const home = await this.request<HomeLayout>("/v1/home/layout/reset", {
      method: "POST",
    });
    invalidateCached(HOME_LAYOUT_CACHE_KEY);
    return home;
  }

  // ── Organization members ──────────────────────────────────────────────

  async getOrganizationMembers(): Promise<OrganizationMember[]> {
    return this.request<OrganizationMember[]>("/v1/organization/members");
  }

  async syncOrganizationMembers(): Promise<{
    synced_from_auth0: number;
    members: OrganizationMember[];
  }> {
    return this.request<{
      synced_from_auth0: number;
      members: OrganizationMember[];
    }>("/v1/organization/sync-members", { method: "POST" });
  }

  async updateMemberRole(
    userId: string,
    role: string,
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      `/v1/organization/members/${encodeURIComponent(userId)}/role`,
      { method: "PUT", body: JSON.stringify({ role }) },
    );
  }

  async removeMember(userId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      `/v1/organization/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  }

  async setMemberSalesPersonLink(
    userId: string,
    salesPersonLink: string | null,
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      `/v1/organization/members/${encodeURIComponent(userId)}/sales-link`,
      {
        method: "PUT",
        body: JSON.stringify({ sales_person_link: salesPersonLink }),
      },
    );
  }

  async setMemberConsigneeLink(
    userId: string,
    consigneeLink: string | null,
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      `/v1/organization/members/${encodeURIComponent(userId)}/consignee-link`,
      {
        method: "PUT",
        body: JSON.stringify({ consignee_link: consigneeLink }),
      },
    );
  }

  async getBookingAssignments(): Promise<UserBookingAssignment[]> {
    return this.request<UserBookingAssignment[]>("/v1/reports/assignments");
  }

  async createBookingAssignment(data: {
    user_id: string;
    field_value: string;
  }): Promise<UserBookingAssignment> {
    return this.request<UserBookingAssignment>("/v1/reports/assignments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteBookingAssignment(id: string): Promise<void> {
    await this.request<void>(
      `/v1/reports/assignments/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  }

  async getAssignmentValues(): Promise<string[]> {
    return this.request<string[]>("/v1/reports/assignment-values");
  }

  async getRestrictions(): Promise<RestrictionConfig[]> {
    return this.request<RestrictionConfig[]>("/v1/restrictions");
  }

  async updateRestriction(
    id: string,
    data: RestrictionUpdate,
  ): Promise<RestrictionConfig> {
    return this.request<RestrictionConfig>(
      `/v1/restrictions/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(data) },
    );
  }

  // ── Kai customer requests (SystemAdmin support triage) ──────────────────
  async backofficeListKaiCustomerRequests(
    filters?: KaiCustomerRequestListFilters,
  ): Promise<KaiCustomerRequest[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.category) params.set("category", filters.category);
    if (filters?.client_key) params.set("client_key", filters.client_key);
    if (filters?.assigned_to) params.set("assigned_to", filters.assigned_to);
    if (filters?.limit !== undefined) params.set("limit", String(filters.limit));
    const qs = params.toString() ? `?${params.toString()}` : "";
    return this.request<KaiCustomerRequest[]>(
      `/v1/backoffice/kai-customer-requests${qs}`,
    );
  }

  async backofficeTriageKaiCustomerRequest(
    id: string,
    body: KaiCustomerRequestTriagePayload,
  ): Promise<KaiCustomerRequest> {
    return this.request<KaiCustomerRequest>(
      `/v1/backoffice/kai-customer-requests/${encodeURIComponent(id)}/triage`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  // ── Pricing tiers ──────────────────────────────────────────────────────
  async getPricingTiers(): Promise<PricingTier[]> {
    return this.request<PricingTier[]>("/v1/pricing-tiers");
  }

  async getPricingTierDimensions(): Promise<PricingTierDimensions> {
    return this.request<PricingTierDimensions>("/v1/pricing-tiers/dimensions");
  }

  async createPricingTier(data: CreatePricingTierPayload): Promise<PricingTier> {
    return this.request<PricingTier>("/v1/pricing-tiers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updatePricingTier(
    id: string,
    data: UpdatePricingTierPayload,
  ): Promise<PricingTier> {
    return this.request<PricingTier>(
      `/v1/pricing-tiers/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(data) },
    );
  }

  async deletePricingTier(id: string): Promise<void> {
    await this.request(`/v1/pricing-tiers/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  async createPricingTierAssignment(
    tierId: string,
    data: CreateAssignmentPayload,
  ): Promise<PricingTierAssignment> {
    return this.request<PricingTierAssignment>(
      `/v1/pricing-tiers/${encodeURIComponent(tierId)}/assignments`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async deletePricingTierAssignment(
    tierId: string,
    assignmentId: string,
  ): Promise<void> {
    await this.request(
      `/v1/pricing-tiers/${encodeURIComponent(tierId)}/assignments/${encodeURIComponent(assignmentId)}`,
      { method: "DELETE" },
    );
  }

  // ── Organization integrations ──────────────────────────────────────────
  async listIntegrations(): Promise<Integration[]> {
    return this.request<Integration[]>("/v1/organization/integrations");
  }

  async getIntegration(id: string): Promise<Integration> {
    return this.request<Integration>(
      `/v1/organization/integrations/${encodeURIComponent(id)}`,
    );
  }

  async createIntegration(data: CreateIntegrationPayload): Promise<Integration> {
    return this.request<Integration>("/v1/organization/integrations", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateIntegration(
    id: string,
    data: UpdateIntegrationPayload,
  ): Promise<Integration> {
    return this.request<Integration>(
      `/v1/organization/integrations/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(data) },
    );
  }

  async updateIntegrationStatus(
    id: string,
    status: IntegrationStatus,
  ): Promise<void> {
    await this.request(
      `/v1/organization/integrations/${encodeURIComponent(id)}/status`,
      { method: "PATCH", body: JSON.stringify({ status }) },
    );
  }

  async deleteIntegration(id: string): Promise<void> {
    await this.request(
      `/v1/organization/integrations/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  }

  // ── Desktop RPA agent management ───────────────────────────────────────
  async createRpaEnrollmentCode(
    integrationId: string,
    data: CreateRpaEnrollmentCodePayload = {},
  ): Promise<RpaEnrollmentCodeResponse> {
    return this.request<RpaEnrollmentCodeResponse>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/rpa/enrollment-codes`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async listRpaAgents(integrationId: string): Promise<RpaAgent[]> {
    return this.request<RpaAgent[]>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/rpa/agents`,
    );
  }

  async listRpaJobs(integrationId: string): Promise<RpaJob[]> {
    return this.request<RpaJob[]>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/rpa/jobs`,
    );
  }

  async createRpaJob(
    integrationId: string,
    data: CreateRpaJobPayload,
  ): Promise<RpaJob> {
    return this.request<RpaJob>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/rpa/jobs`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async listRpaReleases(
    integrationId: string,
  ): Promise<RpaWorkflowRelease[]> {
    return this.request<RpaWorkflowRelease[]>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/rpa/workflow-releases`,
    );
  }

  /** Stored workflow bundles for this org (S3) + zip inventory for selection. */
  async getRpaWorkflowBundles(
    integrationId: string,
    storageKey?: string,
  ): Promise<RpaWorkflowBundlesResponse> {
    const params = storageKey
      ? `?storage_key=${encodeURIComponent(storageKey)}`
      : "";
    return this.request<RpaWorkflowBundlesResponse>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/rpa/workflow-bundles${params}`,
    );
  }

  /** Upload + activate a workflow bundle zip (multipart). */
  async uploadRpaWorkflowBundle(
    integrationId: string,
    payload: UploadRpaWorkflowBundlePayload,
  ): Promise<RpaWorkflowRelease> {
    const url = `${this.baseUrl}/v1/organization/integrations/${encodeURIComponent(
      integrationId,
    )}/rpa/workflow-releases/upload`;
    const form = new FormData();
    form.append("bundle", payload.bundle, payload.bundle.name);
    if (payload.manifest?.trim()) form.append("manifest", payload.manifest);
    if (payload.channel?.trim()) form.append("channel", payload.channel);
    if (payload.version?.trim()) form.append("version", payload.version);
    if (payload.activate !== undefined)
      form.append("activate", String(payload.activate));

    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      const token = await this.getToken();
      authHeaders.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders, // let the browser set the multipart boundary
      body: form,
    });
    if (!response.ok) {
      const body = await response
        .json()
        .catch(() => ({ message: `HTTP ${response.status}` }));
      throw new ApiError(
        body.message || body.error || `HTTP ${response.status}`,
        response.status,
      );
    }
    return response.json();
  }

  // ── Microsoft Graph mailboxes ──────────────────────────────────────────
  async listGraphMailboxes(integrationId: string): Promise<GraphMailbox[]> {
    return this.request<GraphMailbox[]>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/graph/mailboxes`,
    );
  }

  async createGraphMailbox(
    integrationId: string,
    data: CreateGraphMailboxPayload,
  ): Promise<GraphMailbox> {
    return this.request<GraphMailbox>(
      `/v1/organization/integrations/${encodeURIComponent(integrationId)}/graph/mailboxes`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async updateGraphMailbox(
    mailboxId: string,
    data: UpdateGraphMailboxPayload,
  ): Promise<GraphMailbox> {
    return this.request<GraphMailbox>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}`,
      { method: "PATCH", body: JSON.stringify(data) },
    );
  }

  /** List the mailbox's mail folders (flattened tree with depth). */
  async listGraphMailboxFolders(mailboxId: string): Promise<GraphMailFolder[]> {
    const res = await this.request<{ folders: GraphMailFolder[] }>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/folders`,
    );
    return res.folders;
  }

  async deleteGraphMailbox(
    mailboxId: string,
  ): Promise<{ deleted: boolean; messages_deleted: number }> {
    return this.request<{ deleted: boolean; messages_deleted: number }>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}`,
      { method: "DELETE" },
    );
  }

  async getGraphAuthorizeUrl(
    mailboxId: string,
  ): Promise<GraphAuthorizeResponse> {
    return this.request<GraphAuthorizeResponse>(
      `/v1/oauth/microsoft-graph/authorize?mailbox_id=${encodeURIComponent(mailboxId)}`,
    );
  }

  async getGraphAdminConsentUrl(
    integrationId: string,
  ): Promise<GraphAdminConsentResponse> {
    return this.request<GraphAdminConsentResponse>(
      `/v1/oauth/microsoft-graph/admin-consent?integration_id=${encodeURIComponent(integrationId)}`,
    );
  }

  async syncGraphMailbox(mailboxId: string): Promise<GraphSyncResult> {
    return this.request<GraphSyncResult>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/sync`,
      { method: "POST" },
    );
  }

  async fullResyncGraphMailbox(mailboxId: string): Promise<GraphSyncResult> {
    return this.request<GraphSyncResult>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/full-resync`,
      { method: "POST" },
    );
  }

  async getMyGraphMailbox(): Promise<{ mailbox: MyGraphMailboxStatus | null }> {
    return this.request<{ mailbox: MyGraphMailboxStatus | null }>(
      `/v1/users/me/graph-mailbox`,
    );
  }

  async listGraphMessages(
    mailboxId: string,
    limit = 25,
    offset = 0,
    category?: string,
  ): Promise<GraphMessageList> {
    const cat = category
      ? `&category=${encodeURIComponent(category)}`
      : "";
    return this.request<GraphMessageList>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/messages?limit=${limit}&offset=${offset}${cat}`,
    );
  }

  async getGraphMessage(
    mailboxId: string,
    messageId: string,
  ): Promise<GraphMessageDetail> {
    return this.request<GraphMessageDetail>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}`,
    );
  }

  /**
   * Fetch a Graph-mail attachment's bytes with auth. Shared by both the
   * download helper and on-demand PDF preview. Returns the blob plus the
   * server-provided filename (falling back to `fallbackFilename`).
   */
  async fetchGraphAttachmentBlob(
    mailboxId: string,
    messageId: string,
    attachmentId: string,
    fallbackFilename: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `${this.baseUrl}/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      try {
        const token = await this.getToken();
        authHeaders.Authorization = `Bearer ${token}`;
      } catch (err) {
        console.warn("Failed to obtain access token", err);
      }
    }
    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      const detail =
        response.status === 403
          ? "You do not have permission to open this file."
          : response.status === 404
            ? "This file is no longer available."
            : `Failed to load attachment: HTTP ${response.status}`;
      throw new Error(detail);
    }
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = match?.[1] || fallbackFilename;
    const blob = await response.blob();
    return { blob, filename };
  }

  async downloadGraphAttachment(
    mailboxId: string,
    messageId: string,
    attachmentId: string,
    filename: string,
  ): Promise<void> {
    const { blob, filename: name } = await this.fetchGraphAttachmentBlob(
      mailboxId,
      messageId,
      attachmentId,
      filename,
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  }

  async getGraphClassificationStats(
    mailboxId: string,
  ): Promise<GraphClassificationStats> {
    return this.request<GraphClassificationStats>(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/classification-stats`,
    );
  }

  async classifyGraphMailbox(
    mailboxId: string,
  ): Promise<{ started: boolean; already_running: boolean; run_id: string }> {
    return this.request(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/classify`,
      { method: "POST" },
    );
  }

  async backfillGraphMailboxEvents(
    mailboxId: string,
    opts: { dryRun?: boolean } = {},
  ): Promise<{ started: boolean; already_running: boolean; run_id: string }> {
    const query = opts.dryRun ? "?dry_run=true" : "";
    return this.request(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/backfill-events${query}`,
      { method: "POST" },
    );
  }

  async purgeGraphMailboxLegacyEvents(
    mailboxId: string,
    opts: { dryRun?: boolean } = {},
  ): Promise<{ started: boolean; already_running: boolean; run_id: string }> {
    const query = opts.dryRun ? "?dry_run=true" : "";
    return this.request(
      `/v1/organization/integrations/graph/mailboxes/${encodeURIComponent(mailboxId)}/purge-legacy-events${query}`,
      { method: "POST" },
    );
  }

  // ── Sales quotas ───────────────────────────────────────────────────────
  async getSalesQuotas(salesPerson?: string): Promise<SalesQuotaTarget[]> {
    const qs = salesPerson
      ? `?sales_person=${encodeURIComponent(salesPerson)}`
      : "";
    return this.request<SalesQuotaTarget[]>(`/v1/sales-quotas${qs}`);
  }

  async getSalesQuotaProgress(salesPerson?: string): Promise<QuotaProgress[]> {
    const qs = salesPerson
      ? `?sales_person=${encodeURIComponent(salesPerson)}`
      : "";
    return this.request<QuotaProgress[]>(`/v1/sales-quotas/progress${qs}`);
  }

  async getReportSchedules(): Promise<ReportSchedule[]> {
    const resp = await this.request<{ data: ReportSchedule[] }>(
      "/v1/reports/schedules",
    );
    return resp.data;
  }

  // ── Email / custom booking reports ────────────────────────────────────

  async getReportBrowse(): Promise<ReportBrowseResponse> {
    return this.request<ReportBrowseResponse>("/v1/reports/browse");
  }

  async createReportSchedule(
    data: CreateReportSchedulePayload,
  ): Promise<ReportSchedule> {
    return this.request<ReportSchedule>("/v1/reports/schedules", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateReportSchedule(
    id: string,
    data: UpdateReportSchedulePayload,
  ): Promise<ReportSchedule> {
    return this.request<ReportSchedule>(
      `/v1/reports/schedules/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(data) },
    );
  }

  async deleteReportSchedule(id: string): Promise<void> {
    await this.request<void>(
      `/v1/reports/schedules/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  }

  async getReportScheduleBookingsViewUrl(
    scheduleId: string,
  ): Promise<{ path: string }> {
    return this.request<{ path: string }>(
      `/v1/reports/schedules/${encodeURIComponent(scheduleId)}/bookings-view-url`,
    );
  }

  async getReportScheduleSubscribers(scheduleId: string): Promise<string[]> {
    const resp = await this.request<{ schedule_id: string; user_ids: string[] }>(
      `/v1/reports/schedules/${encodeURIComponent(scheduleId)}/subscribers`,
    );
    return resp.user_ids;
  }

  async setReportScheduleSubscribers(
    scheduleId: string,
    userIds: string[],
  ): Promise<string[]> {
    const resp = await this.request<{ schedule_id: string; user_ids: string[] }>(
      `/v1/reports/schedules/${encodeURIComponent(scheduleId)}/subscribers`,
      {
        method: "PUT",
        body: JSON.stringify({ user_ids: userIds }),
      },
    );
    return resp.user_ids;
  }

  async toggleReportSubscription(
    scheduleId: string,
    subscribed: boolean,
  ): Promise<ReportSubscription> {
    return this.request<ReportSubscription>(
      `/v1/reports/schedules/${encodeURIComponent(scheduleId)}/subscribe`,
      {
        method: "POST",
        body: JSON.stringify({ subscribed }),
      },
    );
  }

  /**
   * Generate a one-time XLSX booking dump and trigger a browser download.
   * Throws `EmptyReportError` when the report matched no rows.
   */
  async downloadReportPreview(
    reportType: string,
    date?: string,
    config?: Record<string, unknown> | null,
  ): Promise<void> {
    const url = `${this.baseUrl}/v1/reports/generate-preview`;
    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      const token = await this.getToken();
      authHeaders.Authorization = `Bearer ${token}`;
    }
    const body: Record<string, unknown> = { report_type: reportType };
    if (date) body.date = date;
    if (config) body.config = config;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ message: "Download failed" }));
      throw new ApiError(
        err.message || err.error || `HTTP ${response.status}`,
        response.status,
      );
    }

    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = match?.[1] || `report-${reportType}.xlsx`;
    const blob = await response.blob();
    if (blob.size === 0) {
      const err = new Error(
        "The report matched no rows, so there is nothing to download.",
      );
      err.name = "EmptyReportError";
      throw err;
    }

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  }

  /**
   * Fetch a raw batch attachment (source file) as binary data, used by the Kai
   * chat download chips. Returns the bytes plus the server-provided filename
   * and content type.
   */
  async getAttachmentData(
    batchId: string,
    attachmentId: string,
    fallbackFilename?: string,
  ): Promise<{ data: ArrayBuffer; filename: string; contentType: string }> {
    const url = `${this.baseUrl}/v1/batches/${encodeURIComponent(
      batchId,
    )}/attachments/${encodeURIComponent(attachmentId)}`;
    const authHeaders: Record<string, string> = {};
    if (this.getToken) {
      try {
        const token = await this.getToken();
        authHeaders.Authorization = `Bearer ${token}`;
      } catch (err) {
        console.warn("Failed to obtain access token", err);
      }
    }

    const response = await fetch(url, { headers: authHeaders });
    if (!response.ok) {
      throw new ApiError(
        `Failed to fetch attachment: HTTP ${response.status}`,
        response.status,
      );
    }

    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = match?.[1] || fallbackFilename || "attachment";
    const contentType =
      response.headers.get("content-type") || "application/octet-stream";
    const data = await response.arrayBuffer();
    return { data, filename, contentType };
  }

  // ── Web Push (PWA feed notifications) ─────────────────────────────────

  /** Fetch the server's VAPID public key (null when push is unconfigured). */
  async getVapidPublicKey(): Promise<VapidPublicKeyResponse> {
    return this.request<VapidPublicKeyResponse>("/v1/push/vapid-public-key");
  }

  /** Register (upsert) a browser push subscription for the current user. */
  async subscribePush(
    subscription: PushSubscriptionJSON & { user_agent?: string },
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>("/v1/push/subscribe", {
      method: "PUT",
      body: JSON.stringify(subscription),
    });
  }

  /** Remove a push subscription by its endpoint. */
  async unsubscribePush(endpoint: string): Promise<{ removed: boolean }> {
    return this.request<{ removed: boolean }>("/v1/push/subscribe", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    });
  }

  /** Read the caller's feed push preferences. */
  async getFeedPushPreferences(): Promise<FeedPushPreferences> {
    return this.request<FeedPushPreferences>("/v1/feed/push-preferences");
  }

  /** Update (partial merge) the caller's feed push preferences. */
  async updateFeedPushPreferences(
    patch: Partial<Omit<FeedPushPreferences, "push_supported">>,
  ): Promise<FeedPushPreferences> {
    return this.request<FeedPushPreferences>("/v1/feed/push-preferences", {
      method: "PUT",
      body: JSON.stringify(patch),
    });
  }
}

export const api = new ApiClient(API_BASE_URL);
